// @ts-check

/**
 * Generate styles for headlines
 * @type {Object}
 * @property {HeaderType} hedType
 * @property {boolean} boldSubdek
 * @property {number} width
 * @property {ChartlosOptions} options
 * @return {outerTextStyle}
 */
const getHeadlinesStyles = ({ hedType, boldSubdek, width, options, headline }) => {
  const {
    hedLineHeight,
    dekLineHeight,
    subdekLineHeight,
    hedFontSize,
    dekFontSize,
    subdekFontSize,
    hedLetterSpacing,
    dekLetterSpacing,
    subdekLetterSpacing,
  } = options.styles;
  const isHed = hedType === 'headline';
  const isSubdek = headline === 'subdek';

  const nonHedLineHeight = isSubdek ? subdekLineHeight : dekLineHeight;
  const nonHedFontSize = isSubdek ? subdekFontSize : dekFontSize;
  const nonHedLetterSpacing = isSubdek ? subdekLetterSpacing : dekLetterSpacing;

  const newlineHeight = isHed ? hedLineHeight : nonHedLineHeight;
  const fontType = options.headings.chartFont;

  const [fontFamily, fontWeight] = getFont({
    fontType,
    styles: options.styles,
    section: 'hed',
    isHed,
    boldSubdek,
  });

  const hedColor = getHedColor(options, isHed, headline);
  const isMarketwatch = options.product === 'marketwatch';
  return {
    color: hedColor,
    fontFamily,
    lineHeight: isMarketwatch ? `${newlineHeight}px` : newlineHeight,
    width,
    fontSize: isHed ? `${hedFontSize}px` : `${nonHedFontSize}px`,
    fontWeight,
    letterSpacing: isHed ? hedLetterSpacing : nonHedLetterSpacing,
    textTransform: isSubdek && isMarketwatch ? 'uppercase' : 'none',
  };
};

/**
 * Generate styles for notes
 * @param {FootnoteType} section
 * @param {ChartlosOptions} options
 * @return {outerTextStyle}
 */
const getNotesStyles = (section, options, width) => {
  const { styles, headings, product } = options;
  const {
    color,
    lightColor,

    noteLineHeight: lineHeight,
    noteFontSize: fontSize,
    noteLetterSpacing: letterSpacing,
  } = styles;

  const isDate = section === 'date';
  const fontType = headings.chartFont;
  const isVideo = options.id === 'video' || options.id === 'verticalVideo';

  const [fontFamily, fontWeight] = getFont({ fontType, styles, section: 'note', isDate });

  return {
    color: isDate && product === 'wsj' ? color : lightColor,
    fontFamily,
    lineHeight: product === 'marketwatch' ? `${lineHeight}px` : lineHeight,
    fontSize: `${fontSize}px`,
    fontWeight,
    letterSpacing: isVideo ? '0.025em' : 'normal',
    width,
  };
};

function getFont({ fontType, styles, section, isDate, isHed, boldSubdek }) {
  const isHedSection = section === 'hed';
  switch (fontType) {
    case 'ja':
      return [
        styles.jaFontFamily,
        isHedSection && (isHed || boldSubdek) ? styles.cjkBoldFontWeight : styles.normalFontWeight,
      ];
    case 'ch':
      return [
        styles.chFontFamily,
        isHedSection && (isHed || boldSubdek) ? styles.cjkBoldFontWeight : styles.normalFontWeight,
      ];
    default:
      return isHedSection
        ? [
            styles.hedFontFamily,
            isHed || boldSubdek ? styles.boldFontWeight : styles.normalFontWeight,
          ]
        : [isDate ? styles.labelFontFamily : styles.noteFontFamily, styles.noteFontWeight];
  }
}

/**
 * Get color for headlines
 * @param {ChartlosOptions} options
 * @param {boolean} isHed
 * @param {string} headline
 * @returns
 */
function getHedColor(options, isHed, headline) {
  const isBarronsTitle = headline === 'subdek' && options.product === 'barrons';
  if (isBarronsTitle) return options.styles.lightColor;
  return isHed ? options.styles.specialHedColor : options.styles.color;
}

export { getHeadlinesStyles, getNotesStyles };
